<template>
	<div v-if="isAuth" class="phpmon-notification d-flex align-items-center me-3">
		<div
			class="notification-button"
			:class="{
				'status-online': notificationData.status === 'online',
				'status-warning': notificationData.status === 'warning',
				'status-critical': notificationData.status === 'critical',
				'pulse-slow': notificationData.pulse === 'slow',
				'pulse-fast': notificationData.pulse === 'fast',
			}"
			:title="getTooltipText()"
			@click="handleIconClick"
		>
			<div class="notification-content">
				<i :class="notificationData.icon" class="notification-icon"></i>
				<div class="notification-text">
					<div class="status-text">{{ getStatusText() }}</div>
					<div v-if="serverData.offline_servers > 0" class="server-count">{{ serverData.offline_servers }}/{{ serverData.active_servers }} down</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import restAPI from '../api/index'

export default {
	name: 'PhpMonNotification',
	props: {
		showText: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			notificationData: {
				status: 'online',
				color: 'green',
				pulse: 'none',
				message: 'All websites are live',
				icon: 'fas fa-check-circle',
			},
			serverData: {
				total_servers: 0,
				active_servers: 0,
				online_servers: 0,
				offline_servers: 0,
				offline_server_details: [],
			},
			loading: false,
			error: null,
			updateInterval: null,
		}
	},
	computed: {
		...mapGetters(['isAuth']),
	},
	mounted() {
		if (this.isAuth) {
			this.fetchStatus()
			this.startPolling()
		}
	},
	beforeDestroy() {
		this.stopPolling()
	},
	watch: {
		isAuth(newVal) {
			if (newVal) {
				this.fetchStatus()
				this.startPolling()
			} else {
				this.stopPolling()
			}
		},
	},
	methods: {
		async fetchStatus() {
			if (!this.isAuth || this.loading) {
				return
			}

			this.error = null

			try {
				const response = await restAPI.get('/phpmon/status-summary')

				if (response.data && response.data.success) {
					this.serverData = response.data.data
					this.notificationData = response.data.data.notification
				}
			} catch (error) {
				// Silently handle errors - endpoint may not be available
				console.debug('PHPMon monitoring not available:', error.response?.status)

				// Don't show any error state, just keep default "All Live" state
				// This prevents the component from causing auth issues
			}
		},

		startPolling() {
			// Poll every 30 seconds
			this.updateInterval = setInterval(() => {
				this.fetchStatus()
			}, 30000)
		},

		stopPolling() {
			if (this.updateInterval) {
				clearInterval(this.updateInterval)
				this.updateInterval = null
			}
		},

		handleIconClick() {
			// Show detailed status modal
			const statusText = this.getDetailedStatusText()

			this.$swal
				.fire({
					title: 'Server Monitoring Status',
					html: statusText,
					icon: this.getSwalIcon(),
					showConfirmButton: true,
					confirmButtonText: 'Close',
					showCancelButton: true,
					cancelButtonText: 'View Details',
					cancelButtonColor: '#3b82f6',
					width: '600px',
					// Override global toast settings
					toast: false,
					position: 'center',
					timer: null,
					timerProgressBar: false,
					allowOutsideClick: true,
					allowEscapeKey: true,
					showCloseButton: true,
				})
				.then(result => {
					if (result.dismiss === this.$swal.DismissReason.cancel) {
						// User clicked "View Details" button
						this.$router.push('/monitoring')
					}
				})
		},

		getDetailedStatusText() {
			const { total_servers, active_servers, online_servers, offline_servers, offline_server_details } = this.serverData

			let html = `
				<div class="text-start">
					<p><strong>Total Servers:</strong> ${total_servers}</p>
					<p><strong>Active Monitors:</strong> ${active_servers}</p>
					<p><strong>Online Servers:</strong> ${online_servers}</p>
					<p><strong>Offline Servers:</strong> ${offline_servers}</p>
					<hr>
					<p><strong>Status:</strong> ${this.notificationData.message}</p>
			`

			// Show offline server details if any
			if (offline_server_details && offline_server_details.length > 0) {
				html += '<hr><h6>Offline Servers:</h6><ul style="margin: 0; padding-left: 20px;">'

				offline_server_details.forEach(server => {
					const lastCheck = server.last_check ? new Date(server.last_check).toLocaleString() : 'Unknown'
					html += `<li><strong>${server.label}</strong> (${server.ip}) - Last check: ${lastCheck}</li>`
				})

				html += '</ul>'
			}

			html += '</div>'
			return html
		},

		getStatusText() {
			switch (this.notificationData.status) {
				case 'online':
					return 'All Live'
				case 'warning':
					return 'Issues'
				case 'critical':
					return 'Critical'
				default:
					return 'Unknown'
			}
		},

		getTooltipText() {
			if (this.serverData.offline_servers === 0) {
				return 'All websites are live - Click for details'
			}
			return `${this.serverData.offline_servers} servers offline - Click for details`
		},

		getSwalIcon() {
			switch (this.notificationData.status) {
				case 'online':
					return 'success'
				case 'warning':
					return 'warning'
				case 'critical':
					return 'error'
				default:
					return 'info'
			}
		},
	},
}
</script>

<style scoped>
.phpmon-notification {
	position: relative;
}

.notification-button {
	min-width: 120px;
	height: 50px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 8px;
	cursor: pointer;
	transition: all 0.3s ease;
	border: 2px solid transparent;
	padding: 8px 12px;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.notification-content {
	display: flex;
	align-items: center;
	gap: 8px;
}

.notification-icon {
	font-size: 20px;
	flex-shrink: 0;
}

.notification-text {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	min-width: 0;
}

.status-text {
	font-size: 13px;
	font-weight: 600;
	line-height: 1.1;
	white-space: nowrap;
}

.server-count {
	font-size: 11px;
	font-weight: 400;
	line-height: 1.1;
	opacity: 0.9;
	white-space: nowrap;
}

/* Status Colors */
.status-online {
	background: #10b981;
	color: #ffffff;
	border-color: #059669;
}

.status-online:hover {
	background: #059669;
	transform: translateY(-1px);
	box-shadow: 0 4px 8px rgba(16, 185, 129, 0.3);
}

.status-warning {
	background: #f59e0b;
	color: #ffffff;
	border-color: #d97706;
}

.status-warning:hover {
	background: #d97706;
	transform: translateY(-1px);
	box-shadow: 0 4px 8px rgba(245, 158, 11, 0.3);
}

.status-critical {
	background: #ef4444;
	color: #ffffff;
	border-color: #dc2626;
}

.status-critical:hover {
	background: #dc2626;
	transform: translateY(-1px);
	box-shadow: 0 4px 8px rgba(239, 68, 68, 0.3);
}

/* Error/Unavailable state */
.notification-button:not(.status-online):not(.status-warning):not(.status-critical) {
	background: #6b7280;
	color: #ffffff;
	border-color: #4b5563;
}

.notification-button:not(.status-online):not(.status-warning):not(.status-critical):hover {
	background: #4b5563;
	transform: translateY(-1px);
	box-shadow: 0 4px 8px rgba(107, 114, 128, 0.3);
}

/* Pulse Animations */
.pulse-slow {
	animation: pulse-slow 2s infinite;
}

.pulse-fast {
	animation: pulse-fast 1s infinite;
}

@keyframes pulse-slow {
	0% {
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1), 0 0 0 0 rgba(245, 158, 11, 0.7);
	}
	70% {
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1), 0 0 0 10px rgba(245, 158, 11, 0);
	}
	100% {
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1), 0 0 0 0 rgba(245, 158, 11, 0);
	}
}

@keyframes pulse-fast {
	0% {
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1), 0 0 0 0 rgba(239, 68, 68, 0.7);
	}
	70% {
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1), 0 0 0 10px rgba(239, 68, 68, 0);
	}
	100% {
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1), 0 0 0 0 rgba(239, 68, 68, 0);
	}
}

/* Loading state */
.notification-button.loading {
	opacity: 0.7;
}

.notification-button.loading .notification-icon {
	animation: spin 1s linear infinite;
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

/* Responsive adjustments */
@media (max-width: 992px) {
	.notification-button {
		min-width: 100px;
		height: 45px;
		padding: 6px 10px;
	}

	.notification-icon {
		font-size: 18px;
	}

	.status-text {
		font-size: 12px;
	}

	.server-count {
		font-size: 10px;
	}
}

@media (max-width: 768px) {
	.notification-button {
		min-width: 85px;
		height: 42px;
		padding: 5px 8px;
	}

	.notification-icon {
		font-size: 16px;
	}

	.status-text {
		font-size: 11px;
	}

	.server-count {
		font-size: 9px;
	}

	.notification-content {
		gap: 6px;
	}
}
</style>
